import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '@/lib/apiBase';

const CATEGORIES = ['general', 'order', 'missing_photo', 'payment', 'technical', 'other'];
const SUBJECT_MAX = 200;
const MESSAGE_MAX = 5000;

export const ContactWidget = () => {
  const { user, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!user) return null;

  const reset = () => {
    setCategory('general');
    setSubject('');
    setMessage('');
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subject: subject.trim(), message: message.trim(), category }),
      });
      if (res.status === 429) {
        toast.error(t('contact.rateLimitToast'));
        return;
      }
      if (!res.ok) {
        toast.error(t('contact.errorToast'));
        return;
      }
      toast.success(t('contact.successToast'));
      reset();
      setOpen(false);
    } catch {
      toast.error(t('contact.errorToast'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="lg"
        className="fixed bottom-6 right-6 z-40 rounded-full shadow-2xl bg-primary hover:bg-primary/90 text-white px-5 py-3 h-auto"
        data-testid="contact-fab"
      >
        <MessageCircle className="w-5 h-5 mr-2" />
        {t('contact.help')}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>{t('contact.title')}</DialogTitle>
            <DialogDescription>{t('contact.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">{t('contact.category')}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-secondary border-border mt-1" data-testid="contact-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{t(`contact.cat_${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">{t('contact.subject')}</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
                placeholder={t('contact.subjectPlaceholder')}
                className="bg-secondary border-border mt-1"
                data-testid="contact-subject"
              />
            </div>

            <div>
              <Label className="text-xs">{t('contact.message')}</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                placeholder={t('contact.messagePlaceholder')}
                rows={6}
                className="bg-secondary border-border mt-1 resize-none"
                data-testid="contact-message"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right font-mono">
                {message.length} / {MESSAGE_MAX}
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              {t('contact.respondNote')} <span className="text-foreground">{user.email}</span>
            </p>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>
                {t('contact.cancel')}
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !message.trim()}
                className="bg-primary hover:bg-primary/90"
                data-testid="contact-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t('contact.send')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
